import isEmpty from 'hs-lodash/isEmpty';
import { Iterable } from 'immutable';
import enviro from 'enviro';
// @ts-expect-error not typed yet
import { calculateDiff } from 'ContentEditorUI/utils/diffTools';
import Raven from 'raven-js';
import ReducerDifferManager from './ReducerDifferManager';
const MAX_ACTIONS_SIZE = 20;
const initialStateLookup = {};
export function addInitialStateLookupForDiffedReducer(reducerName, state) {
  initialStateLookup[reducerName] = state;
}
const initialStateBuilderCallbacks = [];
export function setInitialStateForDiffedReducers() {
  initialStateBuilderCallbacks.forEach(callback => {
    callback();
  });
}
function getReducerToReturn(config) {
  if (typeof config === 'string') {
    return config;
  }
  if (ReducerDifferManager.getIsUngatedFor(config.gateName)) {
    return 'immer';
  }
  return 'immutable';
}
const convertFromImmutableIfNeeded = value => Iterable.isIterable(value) ? value.toJS() : value;
function checkDiffAndLog(diff, reducerName, action, ravenConfig, trackedActions) {
  if (!isEmpty(diff)) {
    if (!enviro.deployed()) {
      // If running locally, add some dev logs
      console.warn(`Found a diff in the two ${reducerName} reducers, please check the two states below \n Note, the immutable state has been converted to JS`);
      console.log('Action that led to diff: ', action);
      console.log(diff);
    }
    const message = `Diff found in immer reducer differ for reducer: ${reducerName} - ${action.type}`;
    // Capturing action type separately as a breadcrumb so that it's not lost in case the extra object has too many levels of nesting
    if (ravenConfig.captureBreadcrumb) {
      ravenConfig.captureBreadcrumb({
        message: `${message}/${action.type}`,
        level: 'error'
      });
    } else {
      Raven.captureBreadcrumb({
        message: `${message}/${action.type}`,
        level: 'error'
      });
    }
    if (ravenConfig.captureMessage) {
      ravenConfig.captureMessage(message, {
        extra: {
          diff,
          action,
          trackedActions
        }
      });
    } else {
      Raven.captureMessage(message, {
        extra: {
          diff,
          action,
          trackedActions
        }
      });
    }
    return true;
  }
  return false;
}
function addAction(trackedActions, action) {
  trackedActions.push(action);
  if (trackedActions.length >= MAX_ACTIONS_SIZE) {
    trackedActions.splice(0, 1);
  }
}
function defaultCalculateDiff(immutableStateAsJS, immerState) {
  return calculateDiff(immutableStateAsJS, immerState, '', {
    shouldSerializeSetsAndMaps: true
  });
}
function maybeAddGateLookupForReduxSlice(reducerToReturnConfig, reducerName) {
  if (typeof reducerToReturnConfig === 'string') {
    return;
  }
  const gate = reducerToReturnConfig.gateName;
  ReducerDifferManager.addReduxSliceGateLookup(reducerName, gate);
}
window.__hsReduxDiffer = {};
function immutableToImmerReducer(immutableReducer, immerReducer, {
  reducerToReturn,
  reducerName,
  prodConfig,
  serializeImmutableState = convertFromImmutableIfNeeded,
  computeStateDiff = defaultCalculateDiff,
  ravenConfig = {},
  trackActions
}) {
  let diffedReducerState;
  let hasDiffAlready = false;
  let hasCaughtException = false;
  const initialStateBuilderCallback = () => {
    diffedReducerState = initialStateLookup[reducerName];
  };
  initialStateBuilderCallbacks.push(initialStateBuilderCallback);
  maybeAddGateLookupForReduxSlice(reducerToReturn, reducerName);
  const trackedActions = [];
  return (state, action) => {
    const computedReducerToReturn = getReducerToReturn(reducerToReturn);
    if (enviro.isProd() && enviro.deployed()) {
      const skipComputingDiffOnProd = prodConfig.computeDiffOnProd === 'never' || prodConfig.computeDiffOnProd === 'conditionally' && !prodConfig.condition();
      if (skipComputingDiffOnProd) {
        const reducer = computedReducerToReturn === 'immutable' ? immutableReducer : immerReducer;
        return reducer(state, action);
      }
    }
    if (computedReducerToReturn === 'immer') {
      return immerReducer(state, action);
    }
    let immerState;
    let prevImmerState;
    const prevImmutableState = state;
    const immutableState = immutableReducer(state, action);
    try {
      prevImmerState = diffedReducerState;
      immerState = immerReducer(diffedReducerState, action);
      diffedReducerState = immerState;
    } catch (e) {
      const reducerError = e;
      // If we hit an exception, skip doing any more diffs since the state will likely be different
      hasDiffAlready = true;
      // Only log the error once so we're not spamming the console and Sentry
      if (!hasCaughtException) {
        console.error(new Error(`Exception found in immer reducer: ${reducerName}`));
        console.error(e);
        if (ravenConfig.captureException) {
          ravenConfig.captureException(reducerError, {
            extra: {
              immerReducerSource: reducerName,
              action,
              trackedActions
            }
          });
        } else {
          Raven.captureException(reducerError, {
            extra: {
              immerReducerSource: reducerName,
              action,
              trackedActions
            }
          });
        }
      }
      hasCaughtException = true;
      if (!enviro.deployed()) {
        console.error(e);
      }
      return immutableState;
    }
    const someStateChanged = prevImmerState !== immerState || prevImmutableState !== immutableState;
    if (!someStateChanged) {
      if (trackActions && typeof trackActions !== 'boolean') {
        const extraActions = trackActions.extraActions;
        if (extraActions.includes(action.type)) {
          addAction(trackedActions, action.type);
        }
      }
      // If neither the immutable nor the immer state changed, then just return state
      // and don't compute the diff to save some compute
      return computedReducerToReturn === 'immutable' ? immutableState : immerState;
    }

    // Only log a message to sentry if we haven't seen a diff yet because...
    // if we find one diff, then more than likely, most state changes after that will still result in a diff
    // because the state is already incorrect
    if (!hasDiffAlready) {
      if (trackActions) {
        addAction(trackedActions, action.type);
      }
      // maybe wrap the below in a setTimeout/requestIdleCallback for perf?
      const immutableStateAsJS = serializeImmutableState(immutableState);
      // Need to serialize native JS Sets and Maps because immutable serializes its own Sets and Maps to
      // arrays and objects, respectively. Since we can't compare Sets to arrays nor Maps to objects,
      // we need to serialize the native JS Sets and Map equivalents in our immer (or POJO) reducer
      // to arrays and objects respectively as well
      try {
        const diff = computeStateDiff(immutableStateAsJS, immerState);
        hasDiffAlready = checkDiffAndLog(diff, reducerName, action, ravenConfig, trackedActions);
      } catch (e) {
        console.error(e);
        hasDiffAlready = true;
      }
    }
    window.__hsReduxDiffer[reducerName] = diffedReducerState;
    return computedReducerToReturn === 'immutable' ? immutableState : immerState;
  };
}
export default immutableToImmerReducer;

// Temporary helper for migrating immutable reducers that generate timestamps
// (e.g. Date.now) so that the immutable and immer reducers can return the same one
const SHARED_TIMESTAMPS = {
  saveData: 0,
  emailSaveData: 0
};
export const setSharedTimestamp = (key, timestamp) => {
  SHARED_TIMESTAMPS[key] = timestamp;
};
export const getSharedTimestamp = key => {
  return SHARED_TIMESTAMPS[key];
};